import type { DbPortalZeitreiheEintrag } from '@gql/shared-types';

export const JAHR_MIN = 2004;
export const JAHR_MAX = 2026;
/** Kernzeitraum des Mindestpakets (1 Vertrag + 1 Rechnung je Standort und Jahr). */
export const KERN_VON = 2010;
export const KERN_BIS = 2020;

export type Kategorie = 'bosch' | 'siemens' | 'mehrere' | 'sonstige' | 'ohne_angabe' | 'keine_fassung';

/** Stichtag eines Jahres: 31.12. (Stand zum Jahresende). */
export function stichtag(jahr: number): string {
  return `${jahr}-12-31`;
}

/** Die zum Stichtag maßgebliche Fassung: letzte mit `von` ≤ Stichtag. */
export function fassungZumStichtag(
  zeitreihe: readonly DbPortalZeitreiheEintrag[],
  tag: string,
): DbPortalZeitreiheEintrag | null {
  let treffer: DbPortalZeitreiheEintrag | null = null;
  for (const e of zeitreihe) {
    if (e.von <= tag) treffer = e;
    else break;
  }
  return treffer;
}

export function kategorieAusKonzessionaeren(k: readonly string[]): Exclude<Kategorie, 'keine_fassung'> {
  if (k.length === 0) return 'ohne_angabe';
  if (k.length > 1) return 'mehrere';
  if (k[0] === 'bosch') return 'bosch';
  if (k[0] === 'siemens') return 'siemens';
  return 'sonstige';
}

export function kategorieZumJahr(zeitreihe: readonly DbPortalZeitreiheEintrag[], jahr: number): Kategorie {
  const f = fassungZumStichtag(zeitreihe, stichtag(jahr));
  return f ? kategorieAusKonzessionaeren(f.k) : 'keine_fassung';
}

/** Dezimaljahr (2014-03-01 → ca. 2014,16) für Balkendarstellungen. */
export function dezimaljahr(iso: string): number {
  const [j, m, t] = iso.split('-').map(Number) as [number, number, number];
  const beginn = Date.UTC(j, 0, 1);
  const ende = Date.UTC(j + 1, 0, 1);
  return j + (Date.UTC(j, m - 1, t) - beginn) / (ende - beginn);
}

export interface Segment {
  fassungId: number;
  von: number;
  bis: number;
  kategorie: Exclude<Kategorie, 'keine_fassung'>;
  konzessionaere: string[];
  uebernommen: boolean;
  /** true, wenn das Segment über die letzte bekannte Fassung hinaus fortgeschrieben ist */
  offenesEnde: boolean;
}

/** Zeitstrahl-Segmente, beschnitten auf [min, max+1). */
export function segmente(
  zeitreihe: readonly DbPortalZeitreiheEintrag[],
  min = JAHR_MIN,
  max = JAHR_MAX,
): Segment[] {
  const grenzeEnde = max + 1;
  const aus: Segment[] = [];
  for (const e of zeitreihe) {
    const von = Math.max(dezimaljahr(e.von), min);
    const bis = Math.min(e.bis ? dezimaljahr(e.bis) : grenzeEnde, grenzeEnde);
    if (bis <= von) continue;
    aus.push({
      fassungId: e.f,
      von,
      bis,
      kategorie: kategorieAusKonzessionaeren(e.k),
      konzessionaere: e.k,
      uebernommen: e.u,
      offenesEnde: e.bis === null,
    });
  }
  return aus;
}

export function formatiereDatum(iso: string | null): string {
  if (!iso) return '–';
  const [j, m, t] = iso.split('-');
  return `${t}.${m}.${j}`;
}

export function formatiereMonat(iso: string | null): string {
  if (!iso) return '–';
  const [j, m] = iso.split('-');
  return `${m}.${j}`;
}
